import { Flame, Droplets, Zap, Paintbrush, Wrench, Home, DoorOpen, Layers } from 'lucide-react';

export const TRADE_DATA = {
  hvac: {
    name: 'HVAC',
    slug: 'hvac',
    icon: Flame,
    color: '#ef4444',
    tagline: 'Fill Your HVAC Schedule Year-Round',
    subtitle: 'Exclusive leads for furnace installs, AC repairs, duct work, and maintenance contracts — delivered straight to your calendar.',
    painPoints: [
      'Seasonal slowdowns killing your revenue between peak heating and cooling months',
      'Paying for shared leads that go to 5 other HVAC companies in your area',
      'Missing after-hours emergency calls because nobody is there to answer'
    ],
    features: [
      { title: 'Seasonal Ad Campaigns', desc: 'We run AC ads in summer, furnace ads in winter — always matching homeowner demand in your zip codes.' },
      { title: '24/7 Emergency Capture', desc: 'Our AI answers calls and texts around the clock so you never miss a no-heat or no-AC emergency lead.' },
      { title: 'Maintenance Upsells', desc: 'AI follows up with past customers for seasonal tune-ups, filter changes, and maintenance plan renewals.' }
    ],
    stats: { roas: '584%', revenue: '+$221K', hasRealData: true },
    statsNote: 'Real results from an HVAC campaign managed by Skye AI',
    jobTypes: ['Furnace Install', 'AC Repair', 'Duct Cleaning', 'Heat Pump', 'Maintenance Plans'],
    avgJobValue: 4500,
    benchmarks: { leadToEstimate: 0.60, estimateToClose: 0.30 },
    faqs: [
      { q: 'How do you handle seasonal changes for HVAC?', a: 'We automatically shift your ad campaigns between heating and cooling seasons. In winter, we target furnace replacements and no-heat emergencies. In summer, we push AC installs and repairs. Your ad spend always matches demand.' },
      { q: 'Can you integrate with ServiceTitan?', a: 'Yes. We integrate with ServiceTitan, Housecall Pro, Jobber, and other HVAC-specific CRMs. Leads flow directly into your dispatch board.' },
      { q: 'What areas can you target?', a: 'We target homeowners in your specific service area — down to the zip code. No wasted spend on leads outside your territory.' }
    ]
  },
  roofing: {
    name: 'Roofing',
    slug: 'roofing',
    icon: Home,
    color: '#f59e0b',
    tagline: 'Keep Your Roofing Crews Booked Solid',
    subtitle: 'Exclusive leads for roof replacements, storm damage repairs, and inspections — booked directly on your calendar.',
    painPoints: [
      'Storm chasers flooding your market and driving up lead costs',
      'Wasting thousands on shared leads that 10 other roofers also got',
      'Leads going cold because your crew is too busy on jobs to follow up'
    ],
    features: [
      { title: 'Storm & Season Targeting', desc: 'We ramp up ad spend after storms and during peak roofing season to capture homeowners actively looking for repairs.' },
      { title: 'Instant Lead Follow-Up', desc: 'Our AI texts and calls every lead within 60 seconds — before your competitors even see the notification.' },
      { title: 'Insurance Job Nurturing', desc: 'AI guides homeowners through the insurance claim process, keeping them engaged until the estimate is booked.' }
    ],
    stats: { roas: '1,443%', revenue: null, hasRealData: true },
    statsNote: 'Real ROAS from a roofing campaign managed by Skye AI',
    jobTypes: ['Roof Replacement', 'Storm Damage Repair', 'Roof Inspection', 'Gutter Install', 'Shingle Repair'],
    avgJobValue: 8500,
    benchmarks: { leadToEstimate: 0.55, estimateToClose: 0.25 },
    faqs: [
      { q: 'How do you handle storm season?', a: 'We monitor weather patterns in your area and automatically scale up ad spend after hail storms, high winds, or heavy rain. You get a surge of leads exactly when homeowners need you most.' },
      { q: 'Can you help with insurance restoration leads?', a: 'Absolutely. Our AI nurtures insurance leads through the entire process — from initial contact to claim filing to estimate booking. These longer sales cycles are where most roofers drop the ball.' },
      { q: 'What makes your roofing leads different?', a: 'Every lead is 100% exclusive to you. We never sell the same lead to multiple roofers. Plus, our AI follows up instantly so you connect before any competitor can.' }
    ]
  },
  plumbing: {
    name: 'Plumbing',
    slug: 'plumbing',
    icon: Droplets,
    color: '#3b82f6',
    tagline: 'More Plumbing Jobs, Less Chasing Leads',
    subtitle: 'Exclusive leads for emergency repairs, repiping, water heaters, and bathroom remodels — booked on your schedule.',
    painPoints: [
      'Emergency calls coming in at all hours with nobody to answer',
      'Homeowners calling 3-4 plumbers and going with whoever picks up first',
      'Spending money on ads but not converting leads into booked estimates'
    ],
    features: [
      { title: '24/7 Emergency Response', desc: 'Burst pipes don\'t wait for business hours. Our AI answers every call and text instantly, day or night, and books the emergency visit.' },
      { title: 'Service Area Precision', desc: 'We only target homeowners in your exact service area — no wasted spend on leads 30 miles outside your zone.' },
      { title: 'Upsell Automation', desc: 'After a repair job, our AI follows up about water heater replacements, repipe estimates, and maintenance plans.' }
    ],
    stats: { roas: null, revenue: null, hasRealData: false },
    statsNote: '$987K+ total revenue generated across all Skye AI home service campaigns',
    jobTypes: ['Emergency Repair', 'Water Heater Install', 'Drain Cleaning', 'Repiping', 'Bathroom Remodel'],
    avgJobValue: 3500,
    benchmarks: { leadToEstimate: 0.65, estimateToClose: 0.35 },
    faqs: [
      { q: 'How fast does your AI respond to emergency leads?', a: 'Under 60 seconds. When a homeowner has a burst pipe at 2 AM, our AI answers immediately, gathers details, and books the emergency visit on your calendar.' },
      { q: 'Do you work with residential and commercial plumbers?', a: 'Yes. We tailor your ad campaigns and AI scripts based on whether you focus on residential, commercial, or both. Different messaging for different customers.' },
      { q: 'Can I set my service area boundaries?', a: 'Absolutely. We target down to specific zip codes so you only get leads from homeowners you can actually serve.' }
    ]
  },
  electrical: {
    name: 'Electrical',
    slug: 'electrical',
    icon: Zap,
    color: '#eab308',
    tagline: 'Power Up Your Electrical Business',
    subtitle: 'Exclusive leads for panel upgrades, EV charger installs, rewiring, and emergency electrical work — booked automatically.',
    painPoints: [
      'Big box stores and national chains stealing your local market share',
      'Leads sitting in your voicemail for hours while you\'re on a job site',
      'No consistent lead flow — feast or famine every month'
    ],
    features: [
      { title: 'EV Charger & Panel Upgrade Campaigns', desc: 'We target the booming EV charger and panel upgrade market — homeowners actively searching for these high-ticket services.' },
      { title: 'Job Site Follow-Up', desc: 'When you\'re up a ladder running wire, our AI handles every incoming lead so nothing falls through the cracks.' },
      { title: 'Review Generation', desc: 'After every completed job, our AI automatically requests Google reviews to boost your local ranking.' }
    ],
    stats: { roas: null, revenue: null, hasRealData: false },
    statsNote: '$987K+ total revenue generated across all Skye AI home service campaigns',
    jobTypes: ['Panel Upgrade', 'EV Charger Install', 'Whole-Home Rewire', 'Emergency Repair', 'Lighting Install'],
    avgJobValue: 4000,
    benchmarks: { leadToEstimate: 0.58, estimateToClose: 0.32 },
    faqs: [
      { q: 'Can you target specific electrical services?', a: 'Yes. We can run separate campaigns for high-ticket services like panel upgrades and EV charger installs versus general electrical repair. Different ads, different landing pages, better conversion.' },
      { q: 'How do you handle leads when I\'m on a job site?', a: 'That\'s the whole point. Our AI handles every call and text while you\'re working. It qualifies the lead, answers their questions, and books the estimate on your calendar. You just see a new booking when you check your phone.' },
      { q: 'Do you integrate with electrical contractor software?', a: 'We integrate with ServiceTitan, Housecall Pro, Jobber, GoHighLevel, and 50+ other tools. Your leads flow right into your existing workflow.' }
    ]
  },
  painting: {
    name: 'Painting',
    slug: 'painting',
    icon: Paintbrush,
    color: '#ec4899',
    tagline: 'Book More Painting Jobs Without the Hustle',
    subtitle: 'Exclusive leads for interior painting, exterior painting, cabinet refinishing, and commercial projects — on autopilot.',
    painPoints: [
      'Relying on word-of-mouth and hoping the phone rings',
      'Giving estimates to homeowners who are just price shopping',
      'Seasonal dips in exterior painting work leaving your crew idle'
    ],
    features: [
      { title: 'Interior + Exterior Campaigns', desc: 'We run exterior painting ads in spring/summer and interior painting ads year-round to keep your schedule full in every season.' },
      { title: 'Pre-Qualified Estimates', desc: 'Our AI qualifies leads by asking about square footage, room count, and timeline before booking — so you don\'t waste time on tire-kickers.' },
      { title: 'Portfolio Showcase', desc: 'We build landing pages featuring your best before/after work so homeowners see your quality before they even call.' }
    ],
    stats: { roas: null, revenue: null, hasRealData: false },
    statsNote: '$987K+ total revenue generated across all Skye AI home service campaigns',
    jobTypes: ['Interior Painting', 'Exterior Painting', 'Cabinet Refinishing', 'Deck Staining', 'Commercial Painting'],
    avgJobValue: 3000,
    benchmarks: { leadToEstimate: 0.55, estimateToClose: 0.28 },
    faqs: [
      { q: 'How do you handle seasonal changes for painters?', a: 'We shift your campaigns throughout the year — exterior painting in warmer months, interior projects in winter. Your ad spend always matches when homeowners are buying.' },
      { q: 'Can you help me stop getting price shoppers?', a: 'Yes. Our AI pre-qualifies every lead by asking about their project scope, budget range, and timeline. By the time you show up for the estimate, they\'re serious buyers.' },
      { q: 'Do I need professional photos of my work?', a: 'It helps, but we can work with what you have. Even phone photos of your best before/after work make great ad creative. We handle the design.' }
    ]
  },
  handyman: {
    name: 'Handyman',
    slug: 'handyman',
    icon: Wrench,
    color: '#f97316',
    tagline: 'Keep Your Handyman Business Booked Solid',
    subtitle: 'Exclusive leads for home repairs, odd jobs, and maintenance work — our AI books the estimates while you\'re on the job.',
    painPoints: [
      'Spending more time finding work than doing work',
      'Small jobs not worth the lead cost from big platforms',
      'No system to follow up with past customers for repeat business'
    ],
    features: [
      { title: 'Local Neighborhood Targeting', desc: 'We target homeowners within your exact service radius — every lead is someone you can actually get to.' },
      { title: 'Multi-Service Campaigns', desc: 'We promote all your services — drywall, tile, fixtures, assembly, repairs — with ads tailored to each one.' },
      { title: 'Repeat Customer Engine', desc: 'Our AI follows up with past customers for seasonal maintenance, holiday prep, and new projects.' }
    ],
    stats: { roas: '556%', revenue: '+$328.9K', hasRealData: true },
    statsNote: 'Real results from a handyman campaign managed by Skye AI',
    jobTypes: ['Home Repairs', 'Drywall Patching', 'Fixture Install', 'Furniture Assembly', 'Deck Repair'],
    avgJobValue: 800,
    benchmarks: { leadToEstimate: 0.65, estimateToClose: 0.40 },
    faqs: [
      { q: 'Are the leads big enough for a handyman business?', a: 'We target a mix — from smaller repair jobs that fill gaps in your schedule to bigger projects like deck work and bathroom updates. You tell us your minimum job size and we adjust targeting.' },
      { q: 'How did you generate $328.9K for a handyman business?', a: 'Volume and consistency. We ran targeted ads in the service area, our AI followed up with every single lead, and booked estimates non-stop. At 556% ROAS, every dollar in ads generated over $5 in revenue.' },
      { q: 'Can I set a minimum job size?', a: 'Absolutely. We configure your AI to qualify leads based on job size, so you don\'t waste time on $50 jobs unless you want them.' }
    ]
  },
  'deck-siding': {
    name: 'Deck & Siding',
    slug: 'deck-siding',
    icon: Layers,
    color: '#8b5cf6',
    tagline: 'More Deck & Siding Jobs, Less Wasted Ad Spend',
    subtitle: 'Exclusive leads for deck builds, siding replacement, and exterior renovations — booked directly on your calendar.',
    painPoints: [
      'Short seasonal window to maximize exterior project bookings',
      'Homeowners getting 5+ quotes and going with the cheapest option',
      'Ad platforms eating your budget without delivering quality leads'
    ],
    features: [
      { title: 'Peak Season Blitz', desc: 'We front-load your ad spend during spring and early summer when homeowners are most motivated to start outdoor projects.' },
      { title: 'Material & Style Pre-Qualification', desc: 'Our AI asks about deck size, material preference, and budget range before booking — so your estimates are targeted, not wasted.' },
      { title: 'Neighbor Referral Campaigns', desc: 'After you finish a deck or siding job, we run ads targeting the surrounding neighborhood — "Your neighbor just got a new deck!"' }
    ],
    stats: { roas: '1,070%', revenue: '+$36K', hasRealData: true },
    statsNote: 'Real results from a deck & siding campaign managed by Skye AI',
    jobTypes: ['Deck Build', 'Deck Repair', 'Siding Replacement', 'Pergola Install', 'Fence Install'],
    avgJobValue: 6000,
    benchmarks: { leadToEstimate: 0.52, estimateToClose: 0.27 },
    faqs: [
      { q: 'How do you handle the seasonal nature of deck work?', a: 'We ramp up campaigns in early spring to capture homeowners planning summer projects, run full throttle through summer, and taper off in fall. Your budget follows the demand curve.' },
      { q: 'What does 1,070% ROAS actually mean?', a: 'It means for every $1 spent on ads, the campaign generated $10.70 in revenue. So if you spent $3,000 on ads, you\'d generate over $32,000 in deck and siding jobs.' },
      { q: 'Can you target specific neighborhoods?', a: 'Yes. We can target by zip code, neighborhood, or even radius around your completed projects. Homeowners are much more likely to buy when they see a neighbor got the work done.' }
    ]
  },
  doors: {
    name: 'Door Installation',
    slug: 'doors',
    icon: DoorOpen,
    color: '#10b981',
    tagline: 'Open the Door to More Installation Jobs',
    subtitle: 'Exclusive leads for entry doors, patio doors, interior doors, and garage doors — booked without you lifting a finger.',
    painPoints: [
      'Big box stores dominating the door installation market',
      'Homeowners not knowing they can hire a specialist instead of a chain',
      'Leads from home improvement platforms going to multiple installers'
    ],
    features: [
      { title: 'Specialist Positioning', desc: 'We position you as the local door expert — better quality, better service, better warranty than big box stores.' },
      { title: 'Before/After Ad Creative', desc: 'We showcase your door transformations in ads that stop homeowners mid-scroll and make them want the same upgrade.' },
      { title: 'Multi-Door Upsells', desc: 'When someone books for a front door, our AI mentions interior doors, patio doors, and storm doors to increase job value.' }
    ],
    stats: { roas: null, revenue: '+$42.75K', clients: '45 new clients', hasRealData: true },
    statsNote: 'Real results from a door installation campaign managed by Skye AI',
    jobTypes: ['Entry Door Install', 'Patio Door Install', 'Interior Doors', 'Garage Door', 'Storm Door'],
    avgJobValue: 2500,
    benchmarks: { leadToEstimate: 0.60, estimateToClose: 0.35 },
    faqs: [
      { q: 'How do you compete with big box stores?', a: 'We position you as the premium local expert. Better craftsmanship, personalized service, and no subcontractors. Homeowners pay more for quality when they understand the difference — and our ads make that crystal clear.' },
      { q: 'What results have you gotten for door installers?', a: 'We generated $42.75K in revenue and brought in 45 new clients from a single door installation campaign. That\'s consistent, profitable growth.' },
      { q: 'Can you target homeowners doing renovations?', a: 'Yes. We target homeowners who are actively searching for door replacement, home renovation, and curb appeal upgrades. These are high-intent buyers ready to book.' }
    ]
  }
};

export const TRADE_LIST = Object.values(TRADE_DATA);

export const ROI_DEFAULTS = {
  leadToEstimate: 0.60,
  estimateToClose: 0.30,
};
